import {ExclamationCircleOutlined} from '@ant-design/icons';
import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card, message, Modal, Typography} from 'antd';
import {
  BusinessStatus,
  DocumentType,
  LegalForm,
  PibisiStatus as PibisiStatusEnum,
  UpdateAccountInput,
  useAccountQuery,
  useDocumentsQuery,
  useStartCompanyLookupMutation
} from 'api/generated';
import {AnchorLink, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, Input, Select, SubmitButton, DatePicker} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import humanize from 'humanize-string';
import {formatAmount, formatDate, formatTable, formatUrl} from 'lib/formaters';
import {omitEmpty} from 'lib/omitEmpty';
import {pick} from 'lib/utils';
import moment from 'moment-timezone';
import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {CompanyStatus} from 'routes/Account/CompanyStatus';
import {checkDocNumberInRegistry} from 'routes/Account/utils';
import {checkFromComercia, checkSimplifiedAccount} from '../../lib/accountUtils';
import {PibisiStatus} from './PibisiStatus';
import {SectionProps} from './types';

const {Option} = Select;
const {Text} = Typography;

const allowedFields = ['legalName', 'documentNumber', 'status', 'creationDate'];

export const LegalDetails: React.FC<SectionProps> = ({
  cardProps,
  formProps,
  account,
  accountId,
  onSubmit
}) => {
  const {isAdmin} = useAccessLevel();
  const [isEditing, setEditing] = useState(false);
  const [startCompanyLookup] = useStartCompanyLookupMutation();
  const {data, refetch, loading} = useDocumentsQuery({variables: {accountId}});
  const {startPolling, stopPolling} = useAccountQuery({variables: {id: accountId}});

  const documents = data?.documents.items || [];
  const inRegistry = documents.some((doc) => doc.type === DocumentType.MERCANTILE_REGISTRY_NOTE);
  const isProcessing = account?.business?.status === BusinessStatus.PROCESSING;
  const isSimplified = checkSimplifiedAccount(account);

  useEffect(() => {
    if (isProcessing) {
      startPolling(5000);
    } else {
      stopPolling();
      void refetch();
    }
    return () => stopPolling();
  }, [isProcessing]);

  if (!account) return <Card {...cardProps} title="Business details" />;
  const business = account.business;
  const isCompany = business?.legalForm === LegalForm.COMPANY;
  const pibisiStatus = business?.pibisi?.status;
  const canLookup =
    isAdmin &&
    isCompany &&
    !loading &&
    !inRegistry &&
    !isProcessing &&
    business.country === 'ES' &&
    !checkFromComercia(account) &&
    checkDocNumberInRegistry(business.documentNumber) &&
    business.status !== BusinessStatus.PROCESSING;
  const handleCompanyLookup = async (documentNumber?: string | null) => {
    if (!documentNumber) return;
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Are you sure?',
      content: 'This will request a report from the Registro Mercantil.',
      okText: 'Request',
      onOk: async () => {
        await startCompanyLookup({variables: {documentNumber, accountId: account.id}});
        message.success('The request usually takes less than 5 minutes to process.');
      }
    });
  };

  const initialValues: UpdateAccountInput = {
    id: account.id,
    business: business ? omitEmpty(pick(business, allowedFields)) : {legalForm: LegalForm.COMPANY}
  };

  return (
    <Card
      {...cardProps}
      title="Legal details"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik
          onSubmit={async (values) => {
            await onSubmit(values);
            setEditing(false);
          }}
          enableReinitialize
          initialValues={initialValues}>
          {({setFieldValue}) => {
            return (
              <Form {...formProps}>
                <FormItem name="business.legalName" label="Legal name">
                  <Input name="business.legalName" placeholder="MY COMPANY SL" />
                </FormItem>
                <FormItem name="business.documentNumber" label="Tax Identification Number (CIF)">
                  <Input name="business.documentNumber" placeholder="1234567889" />
                </FormItem>
                <FormItem name="business.creationDate" label="Registration date">
                  <DatePicker
                    name="business.creationDate"
                    format="L"
                    style={{width: '100%'}}
                    allowClear={false}
                    showToday={false}
                    disabledDate={(date) => (date ? date.isAfter(moment()) : false)}
                    onChange={(moment) => {
                      setFieldValue(
                        'business.creationDate',
                        moment ? moment.format('YYYY-MM-DD') : undefined
                      );
                    }}
                  />
                </FormItem>
                <FormItem name="business.status" label="Status">
                  <Select name="business.status">
                    {Object.values(BusinessStatus).map((status) => (
                      <Option value={status}>{humanize(status)}</Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem name={`business.pibisi.status`} label="PIBISI Status">
                  <Select name={`business.pibisi.status`} defaultValue={business?.pibisi?.status}>
                    {Object.values(PibisiStatusEnum).map((status) => (
                      <Option key={status} value={status}>
                        {humanize(status)}
                      </Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem
                  wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                  name="submit">
                  <Stack>
                    <Button onClick={() => setEditing(false)}>Cancel</Button>
                    <SubmitButton>Submit</SubmitButton>
                  </Stack>
                </FormItem>
              </Form>
            );
          }}
        </Formik>
      ) : (
        business && (
          <>
            <PropertyList
              omitEmpty
              emptyText="No legal details provided"
              properties={[
                {label: 'Legal name', value: business.legalName},
                {label: 'Legal description', value: business.legalDescription},
                {
                  label: 'Tax Identification Number (CIF)',
                  value:
                    business.documentNumber &&
                    (canLookup ? (
                      <Stack>
                        <Text copyable={{text: business.documentNumber!}}>
                          <Link to={`/accounts?businessDocumentNumber=${business.documentNumber}`}>
                            {business.documentNumber}
                          </Link>
                        </Text>
                        <Button
                          onClick={() => handleCompanyLookup(business.documentNumber)}
                          size="small">
                          Lookup in Registro Mercantil
                        </Button>
                      </Stack>
                    ) : (
                      <Text copyable={{text: business.documentNumber!}}>
                        <Link to={`/accounts?businessDocumentNumber=${business.documentNumber}`}>
                          {business.documentNumber}
                        </Link>
                      </Text>
                    ))
                },
                {
                  label: 'Status',
                  value: !isSimplified ? <CompanyStatus status={business.status} /> : null
                },
                {
                  label: 'PIBISI Status',
                  value: pibisiStatus && (
                    <PibisiStatus status={pibisiStatus} accountId={accountId} />
                  )
                },
                {
                  label: 'Previous names',
                  value: business.previousNames?.join(', ') || null
                },
                {label: 'Legal address', value: business.legalAddress},
                {label: 'Capital', value: formatAmount(business.capital, 'EUR', 0, true)},
                {
                  label: 'Registration date',
                  value: formatDate(business.creationDate, undefined, true)
                },
                {
                  label: 'Start activity date',
                  value: formatDate(business.startActivityDate, undefined, true)
                },
                {
                  label: 'Last updated in registry',
                  value: formatDate(business.lastUpdateDate, undefined, true)
                },
                {
                  label: 'Registered participants',
                  value: formatTable(business.positions, (position) => [
                    position.name,
                    position.role,
                    <div className="no-wrap">{moment(position.fromDate).fromNow()}</div>
                  ])
                },
                {
                  label: 'Registry records',
                  value: formatTable(business.registryRecords, (record) => [
                    formatUrl(record.url, record.name),
                    record.province,
                    moment(record.date).fromNow()
                  ])
                }
              ]}
            />
          </>
        )
      )}
    </Card>
  );
};
